import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ShieldAlert,
  Lock,
  User,
  KeyRound,
  ArrowRight,
  Eye,
  EyeOff,
  Sparkles,
  Layers,
  Headphones,
  Phone,
  Terminal,
  Copy,
  Check,
  X,
} from "lucide-react";
import axios from "axios";
import { useMasterAdmin } from "../../context/MasterAdminAuthContext";
import CustomerSupportModal from "../../components/CustomerSupportModal";

export default function MasterAdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showResetHelp, setShowResetHelp] = useState(false);
  const [copiedResetCmd, setCopiedResetCmd] = useState(false);

  const { login, masterAdmin } = useMasterAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (masterAdmin) {
      navigate("/master-admin");
    }
  }, [masterAdmin, navigate]);

  useEffect(() => {
    async function checkSetup() {
      try {
        const res = await axios.get("http://localhost:8000/api/master-admin/setup-status");
        if (res.data && res.data.initialized === false) {
          setNeedsSetup(true);
        }
      } catch (e) {
        console.warn("Setup check error:", e);
      }
    }
    checkSetup();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setSubmitting(true);
    const res = await login(username, password);
    setSubmitting(false);
    if (res.success) {
      navigate("/master-admin");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col justify-between selection:bg-amber-500/30">
      {/* Background Decorative Glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-gradient-to-b from-amber-500/10 via-amber-600/5 to-transparent blur-3xl rounded-full" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500/5 blur-3xl rounded-full" />
      </div>

      {/* Top Header Bar */}
      <header className="relative z-10 w-full max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-lg shadow-amber-500/10">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <div className="text-base font-extrabold tracking-tight text-white flex items-center gap-1.5">
              <span>Print</span>
              <span className="text-amber-400">Q</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 font-semibold tracking-wider uppercase ml-1">
                Master Console
              </span>
            </div>
            <div className="text-[11px] text-zinc-400 font-medium">
              JSM DIGITAL SOLUTIONS
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSupportModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-medium text-zinc-300 hover:text-white transition-colors"
          >
            <Headphones className="w-3.5 h-3.5 text-amber-400" />
            <span>Support (9545262332)</span>
          </button>
          <Link
            to="/login"
            className="px-3.5 py-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs text-zinc-400 hover:text-white transition-colors"
          >
            Back to Shop Login
          </Link>
        </div>
      </header>

      {/* Main Login Card */}
      <main className="relative z-10 w-full max-w-md mx-auto px-6 py-8">
        <div className="bg-zinc-900/90 border border-zinc-800/90 backdrop-blur-xl rounded-2xl p-7 shadow-2xl shadow-black/80 flex flex-col gap-6">
          {/* Card Header */}
          <div className="flex flex-col gap-1.5 text-center items-center">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400/20 via-amber-500/10 to-transparent border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-md">
              <KeyRound className="w-6 h-6" />
            </div>
            <h1 className="text-xl font-bold text-white mt-2">
              Master Admin Access
            </h1>
            <p className="text-xs text-zinc-400 max-w-xs leading-relaxed">
              Authenticate with your Master credentials to manage user accounts, memberships, licenses, and system audit logs.
            </p>
          </div>

          {/* First Run Setup Notice */}
          {needsSetup && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3.5 flex items-center justify-between text-xs text-amber-300">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
                <span>First run detected: Master Admin is not yet configured.</span>
              </div>
              <Link
                to="/master-admin/setup"
                className="px-2.5 py-1 rounded-md bg-amber-400 text-zinc-950 font-bold text-[11px] shrink-0 hover:bg-amber-300 transition-colors"
              >
                Set Up Now
              </Link>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-amber-400" />
                <span>Master Admin Username</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  required
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. MasterAdmin"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950/80 border border-zinc-800 focus:border-amber-500/60 focus:ring-2 focus:ring-amber-500/20 text-sm text-white placeholder:text-zinc-600 outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-amber-400" />
                <span>Master Password</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full px-3.5 py-2.5 pr-10 rounded-xl bg-zinc-950/80 border border-zinc-800 focus:border-amber-500/60 focus:ring-2 focus:ring-amber-500/20 text-sm text-white placeholder:text-zinc-600 outline-none transition-all font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 p-1"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs pt-1">
              <button
                type="button"
                onClick={() => setShowResetHelp(true)}
                className="text-[11px] text-zinc-400 hover:text-amber-400 transition-colors flex items-center gap-1"
              >
                <Terminal className="w-3.5 h-3.5 text-amber-400" />
                <span>Forgot / Reset Master Password</span>
              </button>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:brightness-110 active:scale-[0.98] text-zinc-950 font-bold text-sm shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Authenticate Master Console</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="bg-zinc-950/60 border border-zinc-800/80 rounded-xl p-3.5 flex items-start gap-2.5 text-[11px] text-zinc-400 leading-relaxed">
            <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <span>
              Restricted Area. All authentication attempts and administrative modifications are cryptographically signed and recorded to the immutable audit trail.
            </span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full max-w-7xl mx-auto px-6 py-5 text-center text-xs text-zinc-500 border-t border-zinc-900">
        <p>
          PrintQ © {new Date().getFullYear()} JSM DIGITAL SOLUTIONS. Dedicated Scan-to-Print Architecture.
        </p>
      </footer>

      {/* Support Modal */}
      <CustomerSupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />

      {/* Reset Password Instructions Modal */}
      {showResetHelp && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl p-6 flex flex-col gap-4 animate-scale-in">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <Terminal className="w-5 h-5 text-amber-400" />
                <h3 className="text-sm font-bold text-white">Reset Master Password</h3>
              </div>
              <button
                onClick={() => setShowResetHelp(false)}
                className="w-7 h-7 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed">
              For security, Master Admin passwords can only be reset directly by the server operator via the local command-line utility.
            </p>

            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 flex flex-col gap-2">
              <span className="text-[11px] text-zinc-400 font-semibold">Run in Command Prompt / PowerShell:</span>
              <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 px-3 py-2 rounded-lg font-mono text-xs text-amber-300">
                <span className="truncate">python reset_master_admin_password.py</span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText("python reset_master_admin_password.py");
                    setCopiedResetCmd(true);
                    setTimeout(() => setCopiedResetCmd(false), 2000);
                  }}
                  className="ml-2 px-2 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-[10px] text-zinc-200 flex items-center gap-1 shrink-0"
                >
                  {copiedResetCmd ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  <span>{copiedResetCmd ? "Copied" : "Copy"}</span>
                </button>
              </div>
              <span className="text-[10px] text-zinc-500">
                Working directory: <code className="text-zinc-400">c:\Users\dell\Desktop\app\backend</code>
              </span>
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={() => setShowResetHelp(false)}
                className="px-4 py-1.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-xs font-semibold text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
